import { DownloadIcon } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { base64ToBlob } from '../../../lib/utils/base64-to-blob.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { OrderAttachment } from '../hooks/use-order-attachments';

type AttachmentDownloadButtonProps = {
    attachment: OrderAttachment;
};

export const AttachmentDownloadButton: React.FC<AttachmentDownloadButtonProps> = ({ attachment }) => {
    const { t } = useLocale('transaction-details');
    const { toast } = useToast();
    const [isDownloading, setIsDownloading] = useState(false);
    const utils = api.useUtils();
    const sendEvent = useAnalytics();

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDownloading(true);

        try {
            const result = await utils.platoUser.grid.getDetailQuoteOrder.getSalesDocument.fetch({
                documentId: attachment.documentId,
                documentType: attachment.documentType || '',
            });

            const pdfBlob = base64ToBlob(result.pdfBase64, 'application/pdf');
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${attachment.documentName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            sendEvent('transaction_document_downloaded', {
                document_id: attachment.documentId,
                document_type: attachment.documentType || '',
                document_name: attachment.documentName,
            });
        } catch (err) {
            console.error('Error downloading PDF:', err);
            toast({ icon: 'warning', title: t('attachments__error_download') });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Button
            variant="subtle"
            size="icon"
            onClick={handleDownload}
            aria-label={t('attachments__action_download')}
            isLoading={isDownloading}
            disabled={isDownloading}
            icon={DownloadIcon}
        />
    );
};
